/*
 * QuizWidget — sidebar card that appears on the Questions page.
 * Shows a summary of recent exams + link to /tests.
 * Styled like FollowingTags (Card with Card.Header).
 */

import { FC, memo, useEffect, useState } from 'react';
import { Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { tryLoggedAndActivated } from '@/utils/guard';
import { listExams, type ExamSummary } from '@/services/client/quiz';

const QuizWidget: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'practice_tests' });
  const [exams, setExams] = useState<ExamSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listExams()
      .then((data) => {
        if (!cancelled) setExams(data.exams.slice(0, 5));
      })
      .catch(() => {
        // silently ignore — widget is non-critical
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!tryLoggedAndActivated().ok) {
    return null;
  }

  return (
    <Card className="mb-4">
      <Card.Header className="text-nowrap d-flex justify-content-between text-capitalize">
        <span>{t('tests')}</span>
        <Link to="/tests" className="btn btn-link p-0 btn-no-border">
          {t('view_all')}
        </Link>
      </Card.Header>
      <Card.Body>
        {loading && <div className="text-muted">{t('loading')}</div>}
        {!loading && exams.length === 0 && (
          <>
            <div className="text-muted mb-2">{t('no_tests_yet')}</div>
            <Link to="/tests">
              <Button size="sm" variant="outline-primary">
                {t('create_test')}
              </Button>
            </Link>
          </>
        )}
        {!loading && exams.length > 0 && (
          <div className="list-unstyled mb-0">
            {exams.map((exam) => (
              <div key={exam.id} className="mb-2">
                <Link
                  to={
                    // Public tests open on the count/mode picker first.
                    exam.is_public
                      ? `/public-tests/${exam.id}`
                      : `/tests/${exam.id}`
                  }
                  className="text-body text-decoration-none">
                  <div className="text-truncate fw-medium">{exam.title}</div>
                  <small className="text-muted">
                    {t('questions_count', { count: exam.question_count })}
                    {exam.topic ? ` · ${exam.topic}` : ''}
                  </small>
                </Link>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default memo(QuizWidget);
